/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Card, Checkbox, Col, Form, Input, InputNumber, Row, Select, Tooltip, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import RichTextEditor from '../../Fields/RichTextEditor/RichTextEditor';
import EntryPointSelect from '../../Fields/EntryPointSelect';
import { DeleteIcon } from '../../../Icons/DeleteIcon';
import { PlusIcon } from '../../../Icons/PlusIcon';

// Misc imports
import userHasEditAccess from '../../../../utils/misc/hasEditAccess';
import useTheme from '../../../../utils/hooks/useTheme';
import { RICH_TEXT_DEFAULT_TOOLBAR_ACTIONS } from '../../../../constants/markdown';
import openConsoleAlertMessage from '../../../../utils/misc/openConsoleAlertMessage';

// Constant declarations
const SUGGESTED_ACTION_LAYOUTS = [
  'stacked',
  'carousel',
  'flow'
];
const FORM_CONFIGURATION_LAYOUT_PATH = ['settings', 'layout', 'microsoftWebChat', 'configuration'];

const BUBBLE_ACTIONS = {
  NO_BUBBLE_ACTION: 'No bubble action',
  AUTO_POP_OUT: 'Automatic Pop-out',
  USER_PROMPT_BUBBLE: 'Chat Prompt',
  ADVANCED_CHAT_PROMPT_BUBBLE: 'Advanced Chat Prompt'
};

const POP_OUT_ADVANCED_TIME_SETTINGS = {
  NO_ADVANCED_SETTINGS: 'No advanced time settings',
  DOUBLE_TIME: 'Double Pop-out time after every view',
  ONLY_ONCE: 'Pop-out only once'
};

const BotDeploymentsWebchatActionsForm = ({
  formData = {},
  userRoles = {},
  isCreateMode = false,
  form,
  bubbleActionSettingsOption,
  popOutAdvancedTimeSettingsOption,
  setPopOutAdvancedTimeSettingsOption,
  setBubbleActionSettingsOption,
  hasErrors=0,
  shouldMarkAsErroneous
}) => {

  const { t } = useTranslation();
  const { formProps } = useTheme();
  const hasEditAccess = isCreateMode ? true : userHasEditAccess(formData?.tenant?.organizationId, userRoles?.organizationRoles, formData?.tenant.id, userRoles?.tenantRoles, true);
  const [isMenuButtonsEnabled, setIsMenuButtonsEnabled] = useState();

  useEffect(() => {
    if (formData?.settings?.layout?.microsoftWebChat?.configuration?.menuButtons?.length > 0) {
      setIsMenuButtonsEnabled(true);
    } else {
      setIsMenuButtonsEnabled(false);
    }
  }, [formData]);

  useEffect(() => {
    if (!hasErrors) {
      shouldMarkAsErroneous(false);
      return;
    }

    if (
      form.getFieldError([...FORM_CONFIGURATION_LAYOUT_PATH, 'autoPopOut', 'delayInSeconds']).length > 0
      || form.getFieldError([...FORM_CONFIGURATION_LAYOUT_PATH, 'userPromptBubble', 'delayInSeconds']).length > 0
      || form.getFieldError([...FORM_CONFIGURATION_LAYOUT_PATH, 'userPromptBubble', 'message']).length > 0
      || form.getFieldError([...FORM_CONFIGURATION_LAYOUT_PATH, 'advancedChatPromptBubble', 'stepOneMessage']).length > 0
      || form.getFieldError([...FORM_CONFIGURATION_LAYOUT_PATH, 'advancedChatPromptBubble', 'stepOneButtonLabel']).length > 0
      || form.getFieldError([...FORM_CONFIGURATION_LAYOUT_PATH, 'advancedChatPromptBubble', 'stepTwoMessage']).length > 0
    ) {
      shouldMarkAsErroneous(true);
      return;
    }

    const deployments = form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'advancedChatPromptBubble', 'deployments']);

    const hasDeploymentsError = deployments?.some((notificationObj, idx) => {
      if (form.getFieldError([...FORM_CONFIGURATION_LAYOUT_PATH, 'advancedChatPromptBubble', 'deployments', idx, 'icon']).length > 0) {
        return true;
      }
      return false;
    });

    const menuButtons = form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'menuButtons']);

    const hasError = menuButtons?.some((btnObj, idx) => {
      if (
        form.getFieldError([...FORM_CONFIGURATION_LAYOUT_PATH, 'menuButtons', idx, 'title']).length > 0
        || form.getFieldError([...FORM_CONFIGURATION_LAYOUT_PATH, 'menuButtons', idx, 'node']).length > 0
      ) {
        return true;
      }
      return false;
    });

    shouldMarkAsErroneous(hasDeploymentsError || hasError);
  }, [hasErrors, form]);

  const onChangeBubbleAction = (value) => {
    setBubbleActionSettingsOption(value);
    switch (value) {
      case BUBBLE_ACTIONS.AUTO_POP_OUT:
        form.setFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'userPromptBubble'], null);
        form.setFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'advancedChatPromptBubble'], null);
        break;
      case BUBBLE_ACTIONS.USER_PROMPT_BUBBLE:
        form.setFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'autoPopOut'], null);
        form.setFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'advancedChatPromptBubble'], null);
        break;
      case BUBBLE_ACTIONS.ADVANCED_CHAT_PROMPT_BUBBLE:
        form.setFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'userPromptBubble'], null);
        form.setFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'autoPopOut'], null);
        break;
      default:
        form.setFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'userPromptBubble'], null);
        form.setFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'autoPopOut'], null);
        form.setFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'advancedChatPromptBubble'], null);
        break;
    }
  };

  return (
    <Row gutter={formProps.rowGutter}>
      <Col xs={24}>
        <Typography.Text>
          {t('Actions')}
        </Typography.Text>

        <Typography.Paragraph className="fs-sm" type="secondary">
          {t(`Control your agent's behavior with custom action settings.`)}
        </Typography.Paragraph>
      </Col>

      <Col xs={24}>
        <Form.Item
          label={t('Action Layout')}
          name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'suggestedActionLayout']}
        >
          {
            hasEditAccess ? (
              <Select placeholder={t('Select action layout')} getPopupContainer={triggerNode => triggerNode.parentElement}>
                {
                  SUGGESTED_ACTION_LAYOUTS.map((layout, index) => (
                    <Select.Option key={index} value={layout}>
                      {layout}
                    </Select.Option>
                  ))
                }
              </Select>
            ) : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'suggestedActionLayout'])}</Typography.Text>
          }
        </Form.Item>

        <Form.Item
          label={
            <Typography.Text>
              {t('Bubble Action')}
              <Tooltip
                className="ml-1"
                placement="top"
                title={t('Set your agent to automatically pop out after a specified time.')}
              >
                <QuestionCircleOutlined className="fs-sm position-absolute" />
              </Tooltip>
            </Typography.Text>
          }
        >
          {
            hasEditAccess ? (
              <Select
                placeholder={t('Select bubble action')}
                value={bubbleActionSettingsOption}
                onChange={onChangeBubbleAction}
                getPopupContainer={triggerNode => triggerNode.parentElement}
              >
                {
                  Object.keys(BUBBLE_ACTIONS).map((key) => (
                    <Select.Option key={key} value={BUBBLE_ACTIONS[key]}>
                      {BUBBLE_ACTIONS[key]}
                    </Select.Option>
                  ))
                }
              </Select>
            ) : <Typography.Text>{bubbleActionSettingsOption}</Typography.Text>
          }
        </Form.Item>

        <Form.Item
          hidden={bubbleActionSettingsOption !== BUBBLE_ACTIONS.AUTO_POP_OUT}
          disabled={!hasEditAccess}
          shouldUpdate
        >
          <Card
            size="small"
            className="bg-layout-gray shadow-none"
          >
            <Form.Item
              name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'autoPopOut', 'delayInSeconds']}
              label={t('Pop-out Time (sec)')}
              validateTrigger={['onChange', 'onBlur']}
              required={true}
              rules={[
                {
                  required: bubbleActionSettingsOption === BUBBLE_ACTIONS.AUTO_POP_OUT,
                  message: t('Enter a valid time period')
                }
              ]}
            >
              <InputNumber
                className="w-100"
                disabled={!hasEditAccess || bubbleActionSettingsOption !== BUBBLE_ACTIONS.AUTO_POP_OUT}
                type="number"
                min={0}
              />
            </Form.Item>

            <Form.Item
              label={t('Pop-out advanced time settings')}
            >
              {
                hasEditAccess ? (
                  <Select
                    placeholder={t('Select advanced time settings')}
                    value={popOutAdvancedTimeSettingsOption}
                    onChange={setPopOutAdvancedTimeSettingsOption}
                    disabled={bubbleActionSettingsOption !== BUBBLE_ACTIONS.AUTO_POP_OUT}
                    getPopupContainer={triggerNode => triggerNode.parentElement}
                  >
                    {
                      Object.keys(POP_OUT_ADVANCED_TIME_SETTINGS).map((key) => (
                        <Select.Option key={key} value={POP_OUT_ADVANCED_TIME_SETTINGS[key]}>
                          {POP_OUT_ADVANCED_TIME_SETTINGS[key]}
                        </Select.Option>
                      ))
                    }
                  </Select>
                ) : <Typography.Text>{popOutAdvancedTimeSettingsOption}</Typography.Text>
              }
            </Form.Item>
          </Card>
        </Form.Item>

        <Form.Item
          hidden={bubbleActionSettingsOption !== BUBBLE_ACTIONS.USER_PROMPT_BUBBLE}
          disabled={!hasEditAccess}
          shouldUpdate
        >
          <Card
            size="small"
            className="bg-layout-gray shadow-none"
          >
            <Form.Item
              name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'userPromptBubble', 'delayInSeconds']}
              label={t('Pop-out Time (sec)')}
              validateTrigger={['onChange', 'onBlur']}
              required={true}
              rules={[
                {
                  required: bubbleActionSettingsOption === BUBBLE_ACTIONS.USER_PROMPT_BUBBLE,
                  message: t('Enter a valid time period')
                }
              ]}
            >
              <InputNumber
                className="w-100"
                type="number"
                disabled={!hasEditAccess || bubbleActionSettingsOption !== BUBBLE_ACTIONS.USER_PROMPT_BUBBLE}
                min={0}
              />
            </Form.Item>

            <Form.Item
              label={t('Chat Prompt message')}
              name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'userPromptBubble', 'message']}
              validateTrigger={['onChange', 'onBlur']}
              shouldUpdate
              className="mb-0"
              required={true}
              rules={[
                {
                  required: bubbleActionSettingsOption === BUBBLE_ACTIONS.USER_PROMPT_BUBBLE,
                  message: t('Enter a text message')
                }
              ]}
            >
              <RichTextEditor
                toolbarActions={Object.values(RICH_TEXT_DEFAULT_TOOLBAR_ACTIONS).filter((action) => action !== RICH_TEXT_DEFAULT_TOOLBAR_ACTIONS.CUSTOM_VARIABLE)}
                placeholder={t('Enter your message here')}
                shouldAutoFocus={false}
                disabled={!hasEditAccess || bubbleActionSettingsOption !== BUBBLE_ACTIONS.USER_PROMPT_BUBBLE}
              />
            </Form.Item>
          </Card>
        </Form.Item>

        {/* ADVANCED CHAT PROMPT */}
        <Form.Item
          hidden={bubbleActionSettingsOption !== BUBBLE_ACTIONS.ADVANCED_CHAT_PROMPT_BUBBLE}
          disabled={!hasEditAccess}
          shouldUpdate
        >
          <Card
            size="small"
            className="bg-layout-gray shadow-none"
          >
            {/* STEP ONE MESSAGE */}
            <Form.Item
              name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'advancedChatPromptBubble', 'stepOneMessage']}
              label={t('Step one message')}
              validateTrigger={['onChange', 'onBlur']}
              required={true}
              rules={[
                {
                  required: bubbleActionSettingsOption === BUBBLE_ACTIONS.ADVANCED_CHAT_PROMPT_BUBBLE,
                  message: t('Enter a message')
                }
              ]}
            >
              {
                hasEditAccess
                  ? <Input.TextArea
                    autoSize={{ minRows: 2 }}
                    showCount
                    maxLength={200}
                    allowClear
                    placeholder={t('Enter message for step one')}
                  />
                  : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'advancedChatPromptBubble', 'stepOneMessage'])}</Typography.Text>
              }
            </Form.Item>
            {/* ./ STEP ONE MESSAGE */}

            {/* STEP ONE BUTTON LABEL */}
            <Form.Item
              label={t('Step one button label')}
              name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'advancedChatPromptBubble', 'stepOneButtonLabel']}
              validateTrigger={['onChange', 'onBlur']}
              required={true}
              rules={[
                {
                  required: bubbleActionSettingsOption === BUBBLE_ACTIONS.ADVANCED_CHAT_PROMPT_BUBBLE,
                  message: t('Enter a text message')
                }
              ]}
            >
              {
                hasEditAccess
                  ? <Input allowClear placeholder={t('Enter the button name')}
                  />
                  : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'advancedChatPromptBubble', 'stepOneButtonLabel'])}</Typography.Text>
              }
            </Form.Item>
            {/* ./ STEP ONE BUTTON LABEL */}

            {/* STEP TWO MESSAGE */}
            <Form.Item
              name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'advancedChatPromptBubble', 'stepTwoMessage']}
              label={t('Step two message')}
              validateTrigger={['onChange', 'onBlur']}
              required={true}
              rules={[
                {
                  required: bubbleActionSettingsOption === BUBBLE_ACTIONS.ADVANCED_CHAT_PROMPT_BUBBLE,
                  message: t('Enter a message')
                }
              ]}
            >
              {
                hasEditAccess
                  ? <Input.TextArea
                    autoSize={{ minRows: 1 }}
                    showCount
                    maxLength={100}
                    allowClear
                    placeholder={t('Enter message for step two')}
                  />
                  : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'advancedChatPromptBubble', 'stepTwoMessage'])}</Typography.Text>
              }
            </Form.Item>
            {/* ./ STEP TWO MESSAGE */}
            {/* DEPLOYMENT CHANNELS */}
            <Form.List name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'advancedChatPromptBubble', 'deployments']}>
              {(deploymentFields, { add: addDeployment, remove: removeDeployment }) => (
                <>
                  {deploymentFields.map((deploymentField, index) => (
                    <Card
                      size="small"
                      className="bg-layout-light shadow-none position-relative mb-4">
                      <Row gutter={formProps.rowGutter}>
                        <Col span={24}>
                          {/* Deployment URL */}
                          <Form.Item
                            {...deploymentField}
                            label={
                              <Typography.Text>
                                {t('Deployment URL')}
                                <Tooltip
                                  className="ml-1"
                                  placement="top"
                                  title={t('Leave empty for webchat deployment')}
                                >
                                  <QuestionCircleOutlined className="fs-sm position-absolute" />
                                </Tooltip>
                              </Typography.Text>
                            }
                            name={[deploymentField.name, 'url']}
                          >
                            {
                              hasEditAccess
                                ? <Input allowClear placeholder={t('Enter deployment URL')} />
                                : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'advancedChatPromptBubble', 'deployments', deploymentField.name, 'url'])}</Typography.Text>
                            }
                          </Form.Item>
                          {/* ./ Deployment URL */}

                          {/* Conversation Node ID */}
                          <Form.Item
                            className="mb-0"
                            shouldUpdate
                          >
                            {
                              () => (
                                <Form.Item
                                  {...deploymentField}
                                  name={[deploymentField.name, 'icon']}
                                  rules={[{ required: true, message: 'Missing deployment icon' }]}
                                  label={t('Deployment icon')}
                                >
                                  {
                                    hasEditAccess
                                      ? <Input allowClear placeholder={t('Enter deployment icon')} />
                                      : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'advancedChatPromptBubble', 'deployments', deploymentField.name, 'icon'])}</Typography.Text>
                                  }
                                </Form.Item>
                              )
                            }
                          </Form.Item>

                          <Button
                            type="text"
                            onClick={() => removeDeployment(deploymentField.name)}
                            shape="circle"
                            icon={<DeleteOutlined />}
                            className="float-right bordered border-radius-base"
                          />
                        </Col>
                      </Row>

                    </Card>
                  ))}

                  {(deploymentFields.length < 4 && hasEditAccess) && (
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() => {
                          if (deploymentFields.length < 4) {
                            addDeployment();
                          } else {
                            openConsoleAlertMessage({ type: 'warning', message: t('Maximum 4 buttons allowed per notification') });
                          }
                        }}
                        icon={<PlusOutlined />}
                      >
                        {t('Add Button')} ({deploymentFields.length}/4)
                      </Button>
                    </Form.Item>
                  )}
                </>
              )}
            </Form.List>
            {/* DEPLOYMENT CHANNELS */}
          </Card>
        </Form.Item>
        {/* ./ ADVANCED CHAT PROMPT */}

        <Form.Item
          name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'hideUploadButton']}
          valuePropName="checked"
          className="mb-2"
          initialValue={true}
        >
          <Checkbox disabled={!hasEditAccess}>
            <Typography.Text>
              {t('Hide Upload Button')}

              <Tooltip
                className="ml-1"
                placement="top"
                title={t('Check this option if you want to restrict users from uploading files.')}
              >
                <QuestionCircleOutlined className="fs-sm position-absolute" />
              </Tooltip>
            </Typography.Text>
          </Checkbox>
        </Form.Item>

        <Form.Item
          name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'hideEmojiPickerButton']}
          valuePropName="checked"
          className="mb-2"
        >
          <Checkbox disabled={!hasEditAccess}>
            <Typography.Text>
              {t('Hide Emoji Picker Button')}

              <Tooltip
                className="ml-1"
                placement="top"
                title={t('Check this option if you want to restrict users from selecting from available emojis.')}
              >
                <QuestionCircleOutlined className="fs-sm position-absolute" />
              </Tooltip>
            </Typography.Text>
          </Checkbox>
        </Form.Item>

        <Form.Item
          labelCol={{ span: 12 }}
          name="hideTypingIndicator"
          valuePropName="checked"
          className="mb-2"
        >
          <Checkbox disabled={!hasEditAccess}>
            <Typography.Text>
              {t('Hide Typing Indicator')}

              <Tooltip
                className="ml-1"
                placement="top"
                title={t('Show an indicator while the agent is typing.')}
              >
                <QuestionCircleOutlined className="fs-sm position-absolute" />
              </Tooltip>
            </Typography.Text>
          </Checkbox>
        </Form.Item>

        <Form.Item className="mb-2">
          <Checkbox
            onChange={(e) => {
              form.setFieldValue(
                [...FORM_CONFIGURATION_LAYOUT_PATH, 'menuButtons'],
                isMenuButtonsEnabled ? [] : formData?.settings?.layout?.microsoftWebChat?.configuration?.menuButtons);
              setIsMenuButtonsEnabled(e.target.checked);
            }}
            checked={isMenuButtonsEnabled}
            disabled={!hasEditAccess}
          >
            <Typography.Text>
              {t('Enable Menu Buttons')}

              <Tooltip
                className="ml-1"
                placement="top"
                title={t('Add static buttons to your agent to trigger specific flows.')}
              >
                <QuestionCircleOutlined className="fs-sm position-absolute" />
              </Tooltip>
            </Typography.Text>
          </Checkbox>
        </Form.Item>

        <Form.Item>
          <Form.List name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'menuButtons']}>
            {(fields, { add, remove }) => (
              <Fragment>
                {fields.map((field, index) => (
                  <Card
                    size="small"
                    className="bg-layout-gray shadow-none position-relative mb-4"
                    key={field.key}
                  >
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item
                          shouldUpdate={(prevValues, curValues) =>
                            prevValues.area !== curValues.area || prevValues.sights !== curValues.sights
                          }
                        >
                          {
                            () => (
                              <Form.Item
                                {...field}
                                label={t('Button Name')}
                                name={[field.name, 'title']}
                                rules={[{ required: true, message: t('Missing name') }]}
                                className="mb-0"
                              >
                                {
                                  hasEditAccess
                                    ? <Input allowClear placeholder={t('Enter the button name')} />
                                    : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'menuButtons', field.key, 'title'])}</Typography.Text>
                                }
                              </Form.Item>
                            )
                          }
                        </Form.Item>
                      </Col>

                      <Col span={24}>
                        <Form.Item
                          shouldUpdate={(prevValues, curValues) =>
                            prevValues.area !== curValues.area || prevValues.sights !== curValues.sights
                          }
                        >
                          {
                            () => (
                              <Form.Item
                                {...field}
                                name={[field.name, 'node']}
                                rules={[{ required: true, message: t('Missing flow') }]}
                                label={t('Flow Triggered')}
                                className="mb-0"
                              >
                                {
                                  hasEditAccess
                                    ? <EntryPointSelect />
                                    : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'menuButtons', field.key, 'node'])}</Typography.Text>
                                }
                              </Form.Item>
                            )
                          }
                        </Form.Item>
                      </Col>
                    </Row>

                    {
                      hasEditAccess ? (
                        <Button
                          type="text"
                          onClick={() => remove(field.name)}
                          shape="circle"
                          icon={<DeleteIcon />}
                          className="float-right bordered border-radius-base"
                        />
                      ) : null
                    }
                  </Card>
                ))}

                <Form.Item
                  shouldUpdate
                >
                  {
                    hasEditAccess ? (
                      <Button
                        disabled={!isMenuButtonsEnabled}
                        type="dashed"
                        onClick={() => add()}
                        icon={<PlusIcon />}
                      >
                        {t('Add button')}
                      </Button>
                    ) : null
                  }
                </Form.Item>
              </Fragment>
            )}
          </Form.List>
        </Form.Item>
      </Col>
    </Row>
  );
};

BotDeploymentsWebchatActionsForm.propTypes = {
  formData: PropTypes.object,
  isCreateMode: PropTypes.bool,
  userRoles: PropTypes.object,
  form: PropTypes.object,
  bubbleActionSettingsOption: PropTypes.string,
  popOutAdvancedTimeSettingsOption: PropTypes.string,
  setPopOutAdvancedTimeSettingsOption: PropTypes.func,
  setBubbleActionSettingsOption: PropTypes.func,
  hasErrors: PropTypes.number,
  shouldMarkAsErroneous: PropTypes.func
};

export default BotDeploymentsWebchatActionsForm;
